import React, { FC, Ref, useEffect, useState } from 'react';
import { get } from 'lodash';

import { AccountType } from 'src/actions/accounts/accountsResource';
import { FacebookPage } from 'src/models/Facebook/FacebookPage';
import useFacebookLoginTempAuthToken from '../hooks/useFacebookLoginTempAuthToken';
import { allSucceeded } from 'src/presentation/hooks/useResource';

type FbSDKIframeLoaderProps = {
  accountType: AccountType;
  iframeRef: Ref<any>;
  publicUrl: string;
};

/**
 * Load script that loads Facebook js SDK from cluster domain to bypass FB app domain registration.
 * @param accountType
 * @param iframeRef
 * @param publicUrl
 * @constructor
 * @return {JSX.Element}
 */
const FBSDKIframeLoader: FC<FbSDKIframeLoaderProps> = ({ accountType, iframeRef, publicUrl }): JSX.Element | null => {
  const { loadingState: tokenLoadingState, value: authToken } = useFacebookLoginTempAuthToken();
  if (!allSucceeded(tokenLoadingState) || !publicUrl) {
    return null;
  }
  const src = `${publicUrl}/api/facebook/v1/login?userId=${authToken.userId}&token=${authToken.token}&channel=${accountType}`;
  return <iframe ref={iframeRef} src={src} style={{ display: 'none', height: 0, width: 0 }} />;
};

export default FBSDKIframeLoader;

type FBErrorMessage = {
  error: string;
};

/**
 * Add event listener for messages emitted by FBSDKIframeLoader.
 * @param targetOrigin
 */
export function useFBSDKIframeListener({ targetOrigin }): any {
  const [fbPages, setFbPages] = useState<Array<FacebookPage>>();
  const [fbError, setFbError] = useState<FBErrorMessage>();
  useEffect(() => {
    const handler = event => {
      if (event.origin !== targetOrigin) {
        return;
      }
      const data = get(event, 'data.data.data');
      // Temporry logging for debugging
      if (event.data.message) {
        // filter out unnecessary messages
        // eslint-disable-next-line no-console
        console.log(`FBSDKIframeLoader debug data: ${JSON.stringify(event.data)}`);
      }
      switch (event.data.message) {
        case 'login':
          setFbPages(data);
          break;
        case 'error':
          setFbError(data);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [targetOrigin]);
  return [fbPages, setFbPages, fbError, setFbError];
}

export function initiateFBLogin({ iframeRef, targetOrigin }) {
  const current: any = get(iframeRef, 'current');
  if (targetOrigin && current) {
    current.contentWindow.postMessage('login', targetOrigin);
  }
}
